import './contact-previous-exchange.component.ajscss';

import { IContactHistoryService } from '@agent/core/contact/history/contact-history.service';
import { REFRESH_HISTO_INTERACTIONS, REFRESH_INPROCESS_INTERACTIONS } from '@agent/core/contact/history/models/history-models';
import { ModalService } from '@commons/core/ui/modal/modal.service';
import { IModalEvent } from '@commons/core/ui/modal/models/modal-event';
import { ITimelineEntry } from '@commons/history/timeline//models/timeline-entry';
import { ITimelineEntryResult } from '@commons/history/timeline//models/timeline-entry-result';
import { hasChange } from '@commons/util/helper/helper';
import { ng1Destroy } from '@commons/util/utilities/rx';
import { IChangesObject, IComponentController, IComponentOptions } from 'angular';
import { i18n } from 'i18next';
import { distinctUntilChanged, filter, merge, Observable, Subject, Subscription, switchMap, takeUntil, tap } from 'rxjs';

import { Booking, BookingFacade, Customer, isNotNullOrUndefined, MessagingService, Option, unique } from '@contact/upgrade';

import templateUrl from './contact-previous-exchange.component.ajs.html';

const MODULE_NAME = 'contactPreviousExchangeView';
const NB_HISTO_TO_LOAD: number = 25;

class ContactPreviousExchangeCtrl implements IComponentController {
  public name = MODULE_NAME;

  contact$ = this.contactFacade.selected$;
  customer$ = this.contactFacade.selectedCustomer$;

  contact: Booking | undefined;
  customer: Customer | undefined;
  defaultViewInteractions: number;
  histoViewCompany: boolean | undefined;
  activeCustomer: boolean;
  activeCompany: boolean;
  contactCompanyName: string = '';

  firstCall: boolean = true;
  // infinite scrollopenPopin
  busy: boolean = true;
  maxPreviousExchanges: number = 0;
  timelineData: ITimelineEntry[];

  private modalSubscription = Subscription.EMPTY;
  private cancel$ = new Subject<boolean>();

  constructor(
    private readonly $i18next: i18n,
    private readonly contactHistoryService: IContactHistoryService,
    private readonly messaging: MessagingService,
    private readonly modalService: ModalService,
    private readonly contactFacade: BookingFacade
  ) {}

  $onInit(): void {
    if (this.histoViewCompany) {
      if (this.defaultViewInteractions === 1 && this.firstCall) {
        this.firstCall = false;
      }
      this.showNextPreviousExchanges();
    }
    this.contact$.pipe(ng1Destroy(this)).subscribe(contact => (this.contact = contact));
    this.customer$.pipe(ng1Destroy(this)).subscribe(customer => (this.customer = customer));
    // auto refresh
    this.contact$
      .pipe(
        isNotNullOrUndefined(),
        distinctUntilChanged(
          (customerId1, customerId2) => customerId1 === customerId2,
          ({ customerId }) => customerId
        ),
        tap(() => {
          this.busy = false;
          this.timelineData = [];
        }),
        switchMap(contact =>
          merge(
            this.refreshTimelineHisto(contact),
            this.messaging
              .forLocal()
              .listen$<any>(REFRESH_HISTO_INTERACTIONS)
              .pipe(switchMap(next => this.refreshTimelineHisto(contact, next && next['timeline'] ? next['timeline'] : [])))
          )
        ),
        ng1Destroy(this)
      )
      .subscribe();
  }

  $onChanges(changesObj: { [property: string]: IChangesObject }) {
    if (hasChange(changesObj['contactCompanyName'])) {
      this.contactCompanyName = changesObj['contactCompanyName'].currentValue;
    }
    if (hasChange(changesObj['activeCompany'])) {
      this.showNextPreviousExchanges();
    }
  }

  $onDestroy(): void {
    this.modalSubscription.unsubscribe();
    this.cancel$.complete();
  }

  showNextPreviousExchanges(): void {
    this.busy = false;
    this.timelineData = [];
    this.getNextPreviousExchanges(0);
  }

  getNextPreviousExchanges(_from: number): void {
    if (this.busy || !this.contact) {
      return;
    }
    this.busy = true;
    //  let's find all the data
    if (!this.timelineData) {
      this.timelineData = [];
    }
    if (_from >= this.timelineData.length && _from <= this.maxPreviousExchanges) {
      let param;
      param = {
        from: _from,
        size: NB_HISTO_TO_LOAD,
      };
      if (this.activeCompany) {
        param.companyName = this.contactCompanyName;
      } else {
        param.contact = this.contact;
      }
      this.contactHistoryService
        .findHistory(param)
        .pipe(ng1Destroy(this))
        .subscribe(result => {
          this.timelineData = unique(this.timelineData.concat(result.timeline), 'id');
          this.maxPreviousExchanges = result.total;
          this.busy = false;
        });
    } else {
      this.busy = false;
    }
  }

  /**
   * Called when the user selects an element in the contact history
   */
  openPopin(element: ITimelineEntry) {
    const contact = this.contact;
    if (!contact) {
      return;
    }
    // open modal
    this.modalSubscription.unsubscribe();
    this.modalSubscription = this.modalService
      .open({
        keyboard: false,
        backdrop: false,
        fullscreen: true,
        ctrlKeyboard: true,
        component: 'contactContactViewHistoPopin',
        title:
          this.$i18next.t('agent:contact.info.popin.title') +
          ` ${this.customer && this.customer.lastname} ${this.customer && this.customer.firstname}`,
        resolve: {
          selectContactEntry: () => element,
          contactCalling: () => Option.of(this.contact),
        },
      })
      .pipe(
        filter((evtModal: IModalEvent) => evtModal.type === 'close' || evtModal.type === 'reduce'),
        tap(() => this.refreshTimelineInProcess()),
        switchMap(() => this.refreshTimelineHisto(contact)),
        ng1Destroy(this)
      )
      .subscribe();
  }

  private refreshTimelineInProcess(): void {
    this.messaging.forLocal().publish(REFRESH_INPROCESS_INTERACTIONS, undefined);
  }

  private refreshTimelineHisto(contact: Booking, entriesHistoToMatch: ITimelineEntry[] = []): Observable<ITimelineEntryResult> {
    this.cancel$.next(true);
    let input;
    input = {
      entriesToMatch: entriesHistoToMatch,
      from: 0,
      size: NB_HISTO_TO_LOAD,
    };

    if (this.activeCompany) {
      input.companyName = this.contactCompanyName;
    } else {
      input.contact = contact;
    }
    return this.contactHistoryService.findHistory(input).pipe(
      tap(result => {
        this.timelineData = result.timeline;
        this.maxPreviousExchanges = result.total;
      }),
      takeUntil(this.cancel$)
    );
  }
}

export class ContactPreviousExchange implements IComponentOptions {
  templateUrl = templateUrl;
  bindings = {
    defaultViewInteractions: '<',
    histoViewCompany: '<',
    contactCompanyName: '<',
    activeCompany: '<',
  };
  controller = ['$i18next', 'agentHistory', 'messaging', 'modalService', 'contactFacade', ContactPreviousExchangeCtrl];
}

export default MODULE_NAME;
